import { useState } from 'react'
import type { MeetingState } from '@/entities/call'
import { Button } from '@/shared/ui/button'
import { Field, Input } from '@/shared/ui/field'
import { Spinner } from '@/shared/ui/spinner'
import { loadGuestName, saveGuestName } from '../model/guest-name'
import { useGuestJoin } from '../model/use-guest-join'

interface GuestJoinProps {
  token: string
}

export function GuestJoin({ token }: GuestJoinProps) {
  const { status, meetingState, participantState, pollFailed, submit, isSubmitting } =
    useGuestJoin(token)
  // Идёт восстановление cookie-сессии (refresh вкладки) — не мигаем формой имени.
  if (status === 'restoring') {
    return (
      <div className="flex justify-center py-6">
        <Spinner label="Возвращаем вас во встречу" />
      </div>
    )
  }
  if (status === 'waiting') {
    return (
      <GuestWaiting
        meetingState={meetingState}
        participantState={participantState}
        pollFailed={pollFailed}
      />
    )
  }
  return <GuestNameForm token={token} onSubmit={submit} isSubmitting={isSubmitting} />
}

interface GuestNameFormProps {
  token: string
  onSubmit: (name: string) => void
  isSubmitting: boolean
}

function GuestNameForm({ token, onSubmit, isSubmitting }: GuestNameFormProps) {
  // Префилл сохранённым именем: перезаход с тем же именем = тот же спикер.
  const [name, setName] = useState(() => loadGuestName(token))
  const [error, setError] = useState<string>()

  function handleSubmit() {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Введите имя')
      return
    }
    saveGuestName(token, trimmed)
    onSubmit(trimmed)
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
      className="flex flex-col gap-4"
    >
      <Field label="Ваше имя" {...(error ? { error } : {})}>
        {(props) => (
          <Input
            {...props}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Как вас представить"
          />
        )}
      </Field>
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Входим…' : 'Войти во встречу'}
      </Button>
    </form>
  )
}

interface GuestWaitingProps {
  meetingState: MeetingState | undefined
  participantState: 'waiting' | 'joined' | 'left' | undefined
  pollFailed: boolean
}

// Лобби-ожидание с честным статусом: терминальные состояния встречи и отказ
// в допуске показываются текстом, а не вечным спиннером.
function GuestWaiting({ meetingState, participantState, pollFailed }: GuestWaitingProps) {
  if (meetingState === 'ended' || meetingState === 'cancelled') {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <p className="t-h3 text-text-primary">
          {meetingState === 'ended' ? 'Встреча завершилась, пока вы ожидали' : 'Встреча отменена'}
        </p>
        <p className="text-sm text-text-secondary">
          Если встреча продолжается по новой ссылке, попросите её у организатора.
        </p>
      </div>
    )
  }
  if (participantState === 'left') {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <p className="t-h3 text-text-primary">Организатор завершил ваше участие</p>
        <p className="text-sm text-text-secondary">
          Если это ошибка, обновите страницу и войдите ещё раз.
        </p>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Spinner label="Ожидание допуска" />
      <p className="text-sm text-text-secondary">
        {meetingState === 'scheduled'
          ? 'Встреча ещё не началась. Вы в очереди, организатор скоро подключится'
          : 'Организатор скоро впустит вас во встречу'}
      </p>
      {pollFailed && (
        <p className="text-sm text-accent">
          Не удаётся проверить статус. Проверьте соединение, попытки продолжаются
        </p>
      )}
    </div>
  )
}
